/**
 * Pusher Hook for Realtime In-App Notifications
 * 
 * WHEN TO USE:
 * - User has the app/tab OPEN
 * - Shows toast notifications, updates bell icon
 * - No browser permission needed
 * 
 * HOW IT WORKS:
 * 1. Connects to Pusher after user logs in
 * 2. Subscribes to user-specific private channel
 * 3. Authenticates via backend /pusher/auth endpoint
 * 4. Listens for notification events
 * 5. Triggers callbacks when notifications arrive
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import Pusher, { Channel, Authorizer } from 'pusher-js';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import api from '../lib/api'; // Use the standard api instance for auth to benefit from refresh logic

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

interface PusherConfig {
    key: string;
    cluster: string;
}

interface NotificationEvent {
    type: 'created' | 'read' | 'deleted';
    notification?: {
        id: number;
        title: string;
        message: string;
        created_at: string;
        deleted_at: string | null;
    };
    notification_id?: number;
}

interface UsePusherReturn {
    isConnected: boolean;
    isAvailable: boolean;
    error: string | null;
}

/**
 * usePusher Hook
 * 
 * Manages Pusher connection and subscription for realtime notifications.
 * Automatically connects/disconnects based on authentication state.
 * 
 * @param onNotificationCreated - Callback when new notification arrives
 * @param onNotificationRead - Callback when notification is marked read
 * @param onNotificationDeleted - Callback when notification is deleted
 */
export const usePusher = (
    onNotificationCreated?: (notification: any) => void,
    onNotificationRead?: (notificationId: number) => void,
    onNotificationDeleted?: (notificationId: number) => void,

): UsePusherReturn => {
    const { user, authToken } = useAuth();
    const [isConnected, setIsConnected] = useState(false);
    const [isAvailable, setIsAvailable] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const pusherRef = useRef<Pusher | null>(null);
    const channelRef = useRef<Channel | null>(null);
    const configRef = useRef<PusherConfig | null>(null);

    // Store callbacks in refs to avoid reconnecting when functions change
    const callbacksRef = useRef({
        onNotificationCreated,
        onNotificationRead,
        onNotificationDeleted
    });

    useEffect(() => {
        callbacksRef.current = {
            onNotificationCreated,
            onNotificationRead,
            onNotificationDeleted
        };
    }, [onNotificationCreated, onNotificationRead, onNotificationDeleted]);

    // Fetch Pusher configuration
    useEffect(() => {
        const fetchConfig = async () => {
            if (!user || !authToken) return;
            try {
                const response = await axios.get(`${API_BASE_URL}/api/v1/notifications/system-info`);

                if (response.data.pusher?.available) {
                    configRef.current = {
                        key: response.data.pusher.key,
                        cluster: response.data.pusher.cluster
                    };
                    setIsAvailable(true);
                } else {
                    console.warn('Pusher not configured on backend');
                    setIsAvailable(false);
                }
            } catch (err) {
                console.error('Failed to fetch Pusher config:', err);
                setError('Failed to load notification config');
            }
        };

        fetchConfig();
    }, [user, authToken]); // Added authToken to dependencies

    const connectPusher = useCallback(() => {
        // Only proceed if:
        // 1. User is authenticated
        // 2. Pusher is available
        // 3. We have the config
        if (!user || !authToken || !isAvailable || !configRef.current) {
            return;
        }

        // If already connected, disconnect first to prevent multiple connections
        if (pusherRef.current) {
            pusherRef.current.disconnect();
            pusherRef.current = null;
        }

        const config = configRef.current;

        try {
            // Initialize Pusher client with Custom Authorizer
            const pusher = new Pusher(config.key, {
                cluster: config.cluster,
                authorizer: (channel: Channel, options: any): Authorizer => {
                    return {
                        authorize: async (socketId, callback) => {
                            try {
                                // Use URLSearchParams to send as application/x-www-form-urlencoded
                                // This matches the backend's 'request.form()' requirement
                                const params = new URLSearchParams();
                                params.append('socket_id', socketId);
                                params.append('channel_name', channel.name);

                                // Use the app's 'api' instance which has the 401 interceptor & refresh logic
                                const response = await api.post('/notifications/pusher/auth', params, {
                                    headers: {
                                        'Content-Type': 'application/x-www-form-urlencoded'
                                    }
                                });
                                callback(null, response.data);
                            } catch (err: any) {
                                console.error('Pusher auth error:', err);
                                // If it's a 401 and refresh failed, api context will handle redirect.
                                // We stop Pusher from looping here by returning the error.
                                callback(new Error('Unauthorized'), null);
                                setIsConnected(false);
                            }
                        }
                    };
                }
            });

            pusherRef.current = pusher;

            // Subscribe to user-specific private channel
            const channelName = `private-user-${user.id}`;
            const channel = pusher.subscribe(channelName);
            channelRef.current = channel;

            // Listen for connection state
            pusher.connection.bind('connected', () => {
                setIsConnected(true);
                setError(null);
            });

            pusher.connection.bind('disconnected', () => {
                setIsConnected(false);
            });

            pusher.connection.bind('error', (err: any) => {
                // Check for auth errors specifically to prevent infinite loops
                if (err.error?.data?.code === 401 || err.status === 401) {
                    console.error('Pusher authentication failed persistently. Stopping.');
                    pusher.disconnect();
                }
                console.error('Pusher connection error:', err);
                setError('Connection error');
            });

            // ═══════════════════════════════════════════════════════
            // LISTEN FOR EVENTS (NOTIFY CALLBACKS VIA REFS)
            // ═══════════════════════════════════════════════════════

            channel.bind('notification-created', (data: NotificationEvent) => {
                if (data.notification && callbacksRef.current.onNotificationCreated) {
                    callbacksRef.current.onNotificationCreated(data.notification);
                }
            });

            channel.bind('notification-read', (data: NotificationEvent) => {
                if (data.notification_id && callbacksRef.current.onNotificationRead) {
                    callbacksRef.current.onNotificationRead(data.notification_id);
                }
            });

            channel.bind('notification-deleted', (data: NotificationEvent) => {
                if (data.notification_id && callbacksRef.current.onNotificationDeleted) {
                    callbacksRef.current.onNotificationDeleted(data.notification_id);
                }
            });



        } catch (err) {
            console.error('Failed to initialize Pusher:', err);
            setError('Failed to connect to notification service');
        }
    }, [user, authToken, isAvailable]);


    useEffect(() => {
        connectPusher();

        // Cleanup on unmount or dependency change
        return () => {
            if (channelRef.current) {
                channelRef.current.unbind_all();
                // We need the channel name to unsubscribe, which is derived from user.id
                // If user is null during cleanup, we can't unsubscribe specifically.
                // However, pusher.disconnect() will handle all subscriptions.
                if (user) { // Only try to unsubscribe if user is still defined
                    const channelName = `private-user-${user.id}`;
                    pusherRef.current?.unsubscribe(channelName);
                }
            }
            pusherRef.current?.disconnect();
            setIsConnected(false);
        };
    }, [user, authToken, isAvailable, connectPusher]); // Added isAvailable to dependencies

    return {
        isConnected,
        isAvailable,
        error
    };
};
